import React, { useEffect, useState } from "react";
import {
  Box,
  Typography,
  Container,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  TablePagination,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  Stack,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Snackbar,
  Alert,
  Card,
} from "@mui/material";
import { firestore } from "../Firebase";
import Bg from "../Images/about.jpg";
import { Delete, Edit, Add, FileDownload, CheckCircle, Cancel, DeleteForever } from "@mui/icons-material";
import { motion } from "framer-motion";
import { FaUsers, FaUserPlus, FaCheckCircle, FaClock, FaTimesCircle } from "react-icons/fa";
import * as XLSX from "xlsx";
import { saveAs } from "file-saver";

const Submissions = () => {
  const [submissions, setSubmissions] = useState([]);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(5);
  const [open, setOpen] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const [feedbackOpen, setFeedbackOpen] = useState(false);
  const [feedbackMessage, setFeedbackMessage] = useState("");
  const [feedbackSeverity, setFeedbackSeverity] = useState("success");
  const [currentStudent, setCurrentStudent] = useState(null);

  const [fullName, setFullName] = useState("");
  const [studentNumber, setStudentNumber] = useState("");
  const [program, setProgram] = useState("");
  const [yearOfStudy, setYearOfStudy] = useState("");
  const [phoneNumber, setPhoneNumber] = useState("");

  const darkGlassStyle = {
    background: "rgba(0, 0, 36, 0.85)",
    backdropFilter: "blur(20px)",
    border: "1px solid rgba(0, 217, 255, 0.2)",
    boxShadow: "0 8px 32px rgba(0, 0, 0, 0.4)",
  };

  const inputStyle = {
    "& .MuiOutlinedInput-root": {
      backgroundColor: "rgba(255, 255, 255, 0.05)",
      borderRadius: "12px",
      color: "white",
      "& fieldset": { borderColor: "rgba(0, 217, 255, 0.3)" },
      "&:hover fieldset": { borderColor: "rgba(0, 217, 255, 0.5)" },
      "&.Mui-focused fieldset": { borderColor: "#00d9ff" },
    },
    "& .MuiInputLabel-root": { color: "rgba(255, 255, 255, 0.7)" },
    "& .MuiInputLabel-root.Mui-focused": { color: "#00d9ff" },
    "& .MuiSelect-icon": { color: "rgba(255, 255, 255, 0.7)" },
  };

  useEffect(() => {
    const fetchSubmissions = async () => {
      try {
        const snapshot = await firestore.collection("registrations").get();
        const data = snapshot.docs.map((doc) => ({ id: doc.id, ...doc.data() }));
        setSubmissions(data);
      } catch (error) {
        console.error("Error fetching submissions: ", error);
      }
    };
    fetchSubmissions();
  }, []);

  const showFeedback = (message, severity = "success") => {
    setFeedbackMessage(message);
    setFeedbackSeverity(severity);
    setFeedbackOpen(true);
  };

  const openPopup = () => setOpen(true);
  const closePopup = () => {
    setOpen(false);
    resetForm();
  };

  const openEditPopup = (student) => {
    setCurrentStudent(student);
    setFullName(student.fullName);
    setStudentNumber(student.studentNumber);
    setProgram(student.program);
    setYearOfStudy(student.yearOfStudy);
    setPhoneNumber(student.phoneNumber);
    setEditOpen(true);
  };

  const closeEditPopup = () => {
    setEditOpen(false);
    resetForm();
  };

  const resetForm = () => {
    setFullName("");
    setStudentNumber("");
    setProgram("");
    setYearOfStudy("");
    setPhoneNumber("");
    setCurrentStudent(null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const newStudent = { fullName, studentNumber, program, yearOfStudy, phoneNumber };
      const docRef = await firestore.collection("registrations").add(newStudent);
      setSubmissions([...submissions, { id: docRef.id, ...newStudent }]);
      closePopup();
      showFeedback("Member registered successfully!");
    } catch (error) {
      console.error("Error adding student:", error);
      showFeedback("Error registering member", "error");
    }
  };

  const handleEditSubmit = async (e) => {
    e.preventDefault();
    try {
      const updatedStudent = { fullName, studentNumber, program, yearOfStudy, phoneNumber };
      await firestore.collection("registrations").doc(currentStudent.id).update(updatedStudent);
      const updatedSubmissions = submissions.map((student) =>
        student.id === currentStudent.id ? { ...student, ...updatedStudent } : student
      );
      setSubmissions(updatedSubmissions);
      closeEditPopup();
      showFeedback("Member updated successfully!");
    } catch (error) {
      console.error("Error updating student:", error);
      showFeedback("Error updating member", "error");
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Are you sure you want to delete this member?")) return;
    try {
      await firestore.collection("registrations").doc(id).delete();
      setSubmissions(submissions.filter((student) => student.id !== id));
      showFeedback("Member deleted successfully!");
    } catch (error) {
      console.error("Error deleting student:", error);
      showFeedback("Error deleting member", "error");
    }
  };

  const handleDeleteAll = async () => {
    if (submissions.length === 0) {
      showFeedback("No records to delete", "warning");
      return;
    }

    const confirmText = `Are you sure you want to delete ALL ${submissions.length} member records? This action cannot be undone!`;
    if (!window.confirm(confirmText)) return;

    // Double confirmation for safety
    const finalConfirm = window.prompt(`Type "DELETE ALL" to confirm deletion of ${submissions.length} records:`);
    if (finalConfirm !== "DELETE ALL") {
      showFeedback("Deletion cancelled", "info");
      return;
    }

    try {
      // Delete all documents in batch
      const batch = firestore.batch();
      submissions.forEach((student) => {
        const docRef = firestore.collection("registrations").doc(student.id);
        batch.delete(docRef);
      });
      await batch.commit();

      setSubmissions([]);
      setPage(0);
      showFeedback(`All ${submissions.length} records deleted successfully!`);
    } catch (error) {
      console.error("Error deleting all records:", error);
      showFeedback("Error deleting records", "error");
    }
  };

  const handleApprove = async (id) => {
    try {
      await firestore.collection("registrations").doc(id).update({ status: "approved" });
      setSubmissions(submissions.map((s) => (s.id === id ? { ...s, status: "approved" } : s)));
      showFeedback("Member approved successfully!");
    } catch (error) {
      console.error("Error approving member:", error);
      showFeedback("Error approving member", "error");
    }
  };

  const handleReject = async (id) => {
    if (!window.confirm("Are you sure you want to reject this member?")) return;
    try {
      await firestore.collection("registrations").doc(id).update({ status: "rejected" });
      setSubmissions(submissions.map((s) => (s.id === id ? { ...s, status: "rejected" } : s)));
      showFeedback("Member rejected");
    } catch (error) {
      console.error("Error rejecting member:", error);
      showFeedback("Error rejecting member", "error");
    }
  };

  const getStatusChip = (status) => {
    switch (status) {
      case "approved":
        return (
          <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, color: "#22c55e" }}>
            <FaCheckCircle size={14} />
            <span>Approved</span>
          </Box>
        );
      case "rejected":
        return (
          <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, color: "#ef4444" }}>
            <FaTimesCircle size={14} />
            <span>Rejected</span>
          </Box>
        );
      default:
        return (
          <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, color: "#f97316" }}>
            <FaClock size={14} />
            <span>Pending</span>
          </Box>
        );
    }
  };

  const exportToExcel = () => {
    const worksheet = XLSX.utils.json_to_sheet(submissions);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Members");
    XLSX.writeFile(workbook, "ICTAZ_Members.xlsx");
  };

  const exportToWord = () => {
    const template = `
      <html xmlns:o="urn:schemas-microsoft-com:office:office" xmlns:w="urn:schemas-microsoft-com:office:word">
        <head><title>ICTAZ Members</title></head>
        <body>
          <h1>ICTAZ NIPA Chapter - Registered Members</h1>
          <table border="1" style="border-collapse: collapse; width: 100%;">
            <thead>
              <tr style="background-color: #000024; color: white;">
                <th>Full Name</th><th>Student Number</th><th>Program</th><th>Year</th><th>Phone</th>
              </tr>
            </thead>
            <tbody>
              ${submissions.map((s) => `<tr><td>${s.fullName}</td><td>${s.studentNumber}</td><td>${s.program}</td><td>${s.yearOfStudy}</td><td>${s.phoneNumber}</td></tr>`).join("")}
            </tbody>
          </table>
        </body>
      </html>`;
    const blob = new Blob([template], { type: "application/msword" });
    saveAs(blob, "ICTAZ_Members.docx");
  };

  return (
    <Box
      sx={{
        minHeight: "100vh",
        background: `linear-gradient(135deg, rgba(0, 0, 36, 0.95) 0%, rgba(10, 25, 47, 0.9) 50%, rgba(0, 0, 36, 0.95) 100%), url(${Bg})`,
        backgroundSize: "cover",
        backgroundPosition: "center",
        backgroundAttachment: "fixed",
        color: "white",
        pt: { xs: 12, md: 14 },
        pb: 8,
      }}
    >
      <Container maxWidth="xl">
        {/* Header */}
        <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 1 }}>
            <Box sx={{ width: 60, height: 60, borderRadius: "15px", background: "linear-gradient(135deg, #00d9ff, #00b4d8)", display: "flex", alignItems: "center", justifyContent: "center" }}>
              <FaUsers size={30} color="white" />
            </Box>
            <Box>
              <Typography variant="h4" fontWeight={800} sx={{ color: 'white' }}>Registered Members</Typography>
              <Typography variant="body1" sx={{ color: "rgba(255,255,255,0.7)" }}>Manage chapter membership records</Typography>
            </Box>
          </Box>
        </motion.div>

        {/* Action Buttons */}
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5, delay: 0.2 }}>
          <Box sx={{ display: "flex", flexWrap: "wrap", gap: 2, my: 3 }}>
            <Button onClick={openPopup} variant="contained" startIcon={<Add />}
              sx={{ borderRadius: "12px", px: 3, py: 1.2, background: "linear-gradient(135deg, #00d9ff, #00b4d8)", fontWeight: 600, "&:hover": { background: "linear-gradient(135deg, #00b4d8, #0096c7)" } }}>
              Register New Member
            </Button>
            <Button onClick={exportToExcel} variant="contained" startIcon={<FileDownload />}
              sx={{ borderRadius: "12px", px: 3, py: 1.2, background: "linear-gradient(135deg, #10b981, #059669)", fontWeight: 600, "&:hover": { background: "linear-gradient(135deg, #059669, #047857)" } }}>
              Export to Excel
            </Button>
            <Button onClick={exportToWord} variant="contained" startIcon={<FileDownload />}
              sx={{ borderRadius: "12px", px: 3, py: 1.2, background: "linear-gradient(135deg, #3b82f6, #2563eb)", fontWeight: 600, "&:hover": { background: "linear-gradient(135deg, #2563eb, #1d4ed8)" } }}>
              Export to Word
            </Button>
            <Button onClick={handleDeleteAll} variant="contained" startIcon={<DeleteForever />}
              sx={{ borderRadius: "12px", px: 3, py: 1.2, background: "linear-gradient(135deg, #ef4444, #dc2626)", fontWeight: 600, "&:hover": { background: "linear-gradient(135deg, #dc2626, #b91c1c)" } }}>
              Delete All Records
            </Button>
          </Box>
        </motion.div>

        {/* Table */}
        <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, delay: 0.3 }}>
          <Card sx={{ borderRadius: "20px", ...darkGlassStyle, overflow: "hidden" }}>
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow sx={{ background: "linear-gradient(135deg, rgba(0, 217, 255, 0.2), rgba(0, 180, 216, 0.1))" }}>
                    {["Full Name", "Student Number", "Program", "Year", "Phone", "Status", "Actions"].map((header) => (
                      <TableCell key={header} sx={{ color: "#00d9ff", fontWeight: 700, fontSize: "15px", borderBottom: "1px solid rgba(0, 217, 255, 0.2)" }}>{header}</TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {submissions.length > 0 ? (
                    submissions.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage).map((student, index) => (
                      <TableRow key={student.id} sx={{ "&:hover": { background: "rgba(0, 217, 255, 0.05)" }, borderBottom: "1px solid rgba(255,255,255,0.05)" }}>
                        <TableCell sx={{ color: "white", fontSize: "14px", fontWeight: 600 }}>{student.fullName}</TableCell>
                        <TableCell sx={{ color: "rgba(255,255,255,0.8)", fontSize: "14px" }}>{student.studentNumber}</TableCell>
                        <TableCell sx={{ color: "rgba(255,255,255,0.8)", fontSize: "14px" }}>{student.program}</TableCell>
                        <TableCell sx={{ color: "rgba(255,255,255,0.8)", fontSize: "14px" }}>{student.yearOfStudy}</TableCell>
                        <TableCell sx={{ color: "rgba(255,255,255,0.8)", fontSize: "14px" }}>{student.phoneNumber}</TableCell>
                        <TableCell sx={{ fontSize: "14px" }}>{getStatusChip(student.status)}</TableCell>
                        <TableCell>
                          <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
                            {student.status !== "approved" && (
                              <Button size="small" onClick={() => handleApprove(student.id)}
                                sx={{ borderRadius: "8px", minWidth: "auto", px: 1.5, background: "rgba(34, 197, 94, 0.2)", border: "1px solid #22c55e", color: "#22c55e", "&:hover": { background: "rgba(34, 197, 94, 0.3)" } }}
                                title="Approve Member">
                                <CheckCircle fontSize="small" />
                              </Button>
                            )}
                            {student.status !== "rejected" && student.status !== "approved" && (
                              <Button size="small" onClick={() => handleReject(student.id)}
                                sx={{ borderRadius: "8px", minWidth: "auto", px: 1.5, background: "rgba(249, 115, 22, 0.2)", border: "1px solid #f97316", color: "#f97316", "&:hover": { background: "rgba(249, 115, 22, 0.3)" } }}
                                title="Reject Member">
                                <Cancel fontSize="small" />
                              </Button>
                            )}
                            <Button size="small" onClick={() => openEditPopup(student)}
                              sx={{ borderRadius: "8px", minWidth: "auto", px: 1.5, background: "rgba(59, 130, 246, 0.2)", border: "1px solid #3b82f6", color: "#3b82f6", "&:hover": { background: "rgba(59, 130, 246, 0.3)" } }}>
                              <Edit fontSize="small" />
                            </Button>
                            <Button size="small" onClick={() => handleDelete(student.id)}
                              sx={{ borderRadius: "8px", minWidth: "auto", px: 1.5, background: "rgba(239, 68, 68, 0.2)", border: "1px solid #ef4444", color: "#ef4444", "&:hover": { background: "rgba(239, 68, 68, 0.3)" } }}>
                              <Delete fontSize="small" />
                            </Button>
                          </Box>
                        </TableCell>
                      </TableRow>
                    ))
                  ) : (
                    <TableRow>
                      <TableCell colSpan={7} align="center" sx={{ color: "rgba(255,255,255,0.6)", py: 4 }}>No members registered yet.</TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
            <TablePagination
              rowsPerPageOptions={[5, 10, 25]}
              component="div"
              count={submissions.length}
              rowsPerPage={rowsPerPage}
              page={page}
              onPageChange={(e, newPage) => setPage(newPage)}
              onRowsPerPageChange={(e) => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); }}
              sx={{ color: "white", borderTop: "1px solid rgba(0, 217, 255, 0.2)", "& .MuiTablePagination-selectIcon": { color: "white" } }}
            />
          </Card>
        </motion.div>

        {/* Add Dialog */}
        <Dialog open={open} onClose={closePopup} fullWidth maxWidth="sm" PaperProps={{ sx: { borderRadius: "20px", ...darkGlassStyle } }}>
          <DialogTitle sx={{ color: "white", borderBottom: "1px solid rgba(0, 217, 255, 0.2)", display: "flex", alignItems: "center", gap: 2 }}>
            <Box sx={{ width: 40, height: 40, borderRadius: "10px", background: "linear-gradient(135deg, #00d9ff, #00b4d8)", display: "flex", alignItems: "center", justifyContent: "center" }}>
              <FaUserPlus size={20} color="white" />
            </Box>
            Register New Member
          </DialogTitle>
          <DialogContent sx={{ mt: 2 }}>
            <form onSubmit={handleSubmit}>
              <Stack spacing={2.5} sx={{ mt: 1 }}>
                <TextField label="Full Name" value={fullName} onChange={(e) => setFullName(e.target.value)} required fullWidth sx={inputStyle} />
                <TextField label="Student Number" value={studentNumber} onChange={(e) => setStudentNumber(e.target.value)} required fullWidth sx={inputStyle} />
                <FormControl fullWidth sx={inputStyle}>
                  <InputLabel>Program of Study</InputLabel>
                  <Select value={program} onChange={(e) => setProgram(e.target.value)} label="Program of Study" required
                    MenuProps={{ PaperProps: { sx: { bgcolor: "#0a192f", "& .MuiMenuItem-root": { color: "white", "&:hover": { bgcolor: "rgba(0, 217, 255, 0.2)" } } } } }}>
                    <MenuItem value="Dip in Computer Science">Dip in Computer Science</MenuItem>
                    <MenuItem value="Bsc Computer Science">Bsc Computer Science</MenuItem>
                    <MenuItem value="Dip Information Systems">Dip Information Systems</MenuItem>
                    <MenuItem value="Bsc Information Systems">Bsc Information Systems</MenuItem>
                    <MenuItem value="other">Other</MenuItem>
                  </Select>
                </FormControl>
                <FormControl fullWidth sx={inputStyle}>
                  <InputLabel>Year of Study</InputLabel>
                  <Select value={yearOfStudy} onChange={(e) => setYearOfStudy(e.target.value)} label="Year of Study" required
                    MenuProps={{ PaperProps: { sx: { bgcolor: "#0a192f", "& .MuiMenuItem-root": { color: "white", "&:hover": { bgcolor: "rgba(0, 217, 255, 0.2)" } } } } }}>
                    {["1:1", "1:2", "2:1", "2:2", "3:1", "3:2", "4:1", "4:2"].map((year) => (<MenuItem key={year} value={year}>{year}</MenuItem>))}
                  </Select>
                </FormControl>
                <TextField label="Phone Number" value={phoneNumber} onChange={(e) => setPhoneNumber(e.target.value)} required fullWidth sx={inputStyle} />
                <Button type="submit" variant="contained" fullWidth
                  sx={{ py: 1.5, borderRadius: "12px", background: "linear-gradient(135deg, #00d9ff, #00b4d8)", fontWeight: 600, fontSize: "1rem", "&:hover": { background: "linear-gradient(135deg, #00b4d8, #0096c7)" } }}>
                  Register Member
                </Button>
              </Stack>
            </form>
          </DialogContent>
        </Dialog>

        {/* Edit Dialog */}
        <Dialog open={editOpen} onClose={closeEditPopup} fullWidth maxWidth="sm" PaperProps={{ sx: { borderRadius: "20px", ...darkGlassStyle } }}>
          <DialogTitle sx={{ color: "white", borderBottom: "1px solid rgba(0, 217, 255, 0.2)", display: "flex", alignItems: "center", gap: 2 }}>
            <Box sx={{ width: 40, height: 40, borderRadius: "10px", background: "linear-gradient(135deg, #10b981, #059669)", display: "flex", alignItems: "center", justifyContent: "center" }}>
              <Edit sx={{ color: "white" }} />
            </Box>
            Edit Member
          </DialogTitle>
          <DialogContent sx={{ mt: 2 }}>
            <form onSubmit={handleEditSubmit}>
              <Stack spacing={2.5} sx={{ mt: 1 }}>
                <TextField label="Full Name" value={fullName} onChange={(e) => setFullName(e.target.value)} required fullWidth sx={inputStyle} />
                <TextField label="Student Number" value={studentNumber} onChange={(e) => setStudentNumber(e.target.value)} required fullWidth sx={inputStyle} />
                <FormControl fullWidth sx={inputStyle}>
                  <InputLabel>Program of Study</InputLabel>
                  <Select value={program} onChange={(e) => setProgram(e.target.value)} label="Program of Study" required
                    MenuProps={{ PaperProps: { sx: { bgcolor: "#0a192f", "& .MuiMenuItem-root": { color: "white", "&:hover": { bgcolor: "rgba(0, 217, 255, 0.2)" } } } } }}>
                    <MenuItem value="Dip in Computer Science">Dip in Computer Science</MenuItem>
                    <MenuItem value="Bsc Computer Science">Bsc Computer Science</MenuItem>
                    <MenuItem value="Dip Information Systems">Dip Information Systems</MenuItem>
                    <MenuItem value="Bsc Information Systems">Bsc Information Systems</MenuItem>
                    <MenuItem value="other">Other</MenuItem>
                  </Select>
                </FormControl>
                <FormControl fullWidth sx={inputStyle}>
                  <InputLabel>Year of Study</InputLabel>
                  <Select value={yearOfStudy} onChange={(e) => setYearOfStudy(e.target.value)} label="Year of Study" required
                    MenuProps={{ PaperProps: { sx: { bgcolor: "#0a192f", "& .MuiMenuItem-root": { color: "white", "&:hover": { bgcolor: "rgba(0, 217, 255, 0.2)" } } } } }}>
                    {["1:1", "1:2", "2:1", "2:2", "3:1", "3:2", "4:1", "4:2"].map((year) => (<MenuItem key={year} value={year}>{year}</MenuItem>))}
                  </Select>
                </FormControl>
                <TextField label="Phone Number" value={phoneNumber} onChange={(e) => setPhoneNumber(e.target.value)} required fullWidth sx={inputStyle} />
                <Button type="submit" variant="contained" fullWidth
                  sx={{ py: 1.5, borderRadius: "12px", background: "linear-gradient(135deg, #10b981, #059669)", fontWeight: 600, fontSize: "1rem", "&:hover": { background: "linear-gradient(135deg, #059669, #047857)" } }}>
                  Update Member
                </Button>
              </Stack>
            </form>
          </DialogContent>
        </Dialog>

        {/* Feedback Snackbar */}
        <Snackbar open={feedbackOpen} autoHideDuration={4000} onClose={() => setFeedbackOpen(false)} anchorOrigin={{ vertical: "bottom", horizontal: "right" }}>
          <Alert onClose={() => setFeedbackOpen(false)} severity={feedbackSeverity} sx={{ width: "100%", borderRadius: "12px" }}>{feedbackMessage}</Alert>
        </Snackbar>
      </Container>
    </Box>
  );
};

export default Submissions;
